import React, { useEffect, useState } from 'react';
import { useAppStore } from '@/store';
import { TelegramUtils } from '@/utils/telegram';
import { api } from '@/api';
import logoLight from '@/assets/itinder.svg';
import logoDark from '@/assets/itinder_dark.svg';

const ITChoiceScreen: React.FC = () => {
  const { setActiveScreen, setOnboardingStep, setUser } = useAppStore();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    TelegramUtils.configureBackButton(false);
    TelegramUtils.configureMainButton({ visible: false });
  }, []);

  const logo = TelegramUtils.isLightTheme ? logoDark : logoLight;

  const handleITChoice = async (isIT: boolean) => {
    TelegramUtils.haptic.light();
    setLoading(true);

    try {
      // Update IT status via API
      await (api as any).updateBasicInfo({
        is_it: isIT,
      });
      
      console.log('✅ IT status updated via API:', isIT);
      
      // Get updated profile
      const profile = await api.getMyProfile();
      setUser(profile);
      
      console.log('📊 Profile after IT choice:', {
        gender: profile.gender,
        is_it: profile.is_it,
        status: profile.status,
      });
      
      // Reset onboarding to step 0 and go to onboarding
      setOnboardingStep(0);
      setActiveScreen('onboarding');
      
      TelegramUtils.haptic.success();
    } catch (error) {
      console.error('Failed to set IT status:', error);
      await TelegramUtils.showAlert('Ошибка при сохранении выбора. Проверьте подключение к серверу.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-tg-bg">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-400">Сохраняем выбор...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-tg-bg p-6 safe-area-y">
      {/* Logo */}
      <div className="mb-8">
        <img 
          src={logo} 
          alt="ITinder" 
          className="w-24 h-auto mb-4"
        />
      </div>

      {/* Main Question */}
      <div className="text-center mb-12">
        <h2 className="text-2xl font-bold text-white mb-3 leading-tight">
          Вы работаете в IT?
        </h2>
        <p className="text-lg text-neutral-400">
          Выберите свой статус
        </p>
      </div>

      {/* IT Choice Options */}
      <div className="w-full max-w-sm space-y-4">
        {/* Yes, I'm IT */}
        <button
          onClick={() => handleITChoice(true)}
          className="w-full group"
        >
          <div className="bg-white/5 border-2 border-white/20 rounded-2xl p-6 hover:bg-white/10 hover:border-white/40 transition-all duration-300 active:scale-95">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                <span className="text-3xl">💻</span>
              </div>
              <div className="text-left flex-1">
                <h3 className="text-xl font-bold text-white mb-3">
                  Да, я IT-специалист
                </h3>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                    <p className="text-sm text-neutral-400">Укажу специализацию</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                    <p className="text-sm text-neutral-400">Пройду тест навыков</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                    <p className="text-sm text-neutral-400">Получу бейдж уровня</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </button>

        {/* No, I'm not IT */}
        <button
          onClick={() => handleITChoice(false)}
          className="w-full group"
        >
          <div className="bg-white/5 border-2 border-white/20 rounded-2xl p-6 hover:bg-white/10 hover:border-white/40 transition-all duration-300 active:scale-95">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                <span className="text-3xl">👤</span>
              </div>
              <div className="text-left flex-1">
                <h3 className="text-xl font-bold text-white mb-3">
                  Нет, я не из IT
                </h3>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                    <p className="text-sm text-neutral-400">Можно сразу начать</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                    <p className="text-sm text-neutral-400">Тесты не требуются</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
};

export default ITChoiceScreen;

